/**
 * useSanctaThread — the SSE streaming + message + conversation state hook
 * for the Sancta chatbot. Owns:
 *   · messages array + input field
 *   · conversation_id (server-recalled for auth users, localStorage for guests)
 *   · streaming flag + AbortController
 *   · detection event from the last SSE `event: lang` frame
 *   · SSE parser (event: lang / data: text / data: [[END]])
 *   · initial history hydration on widget open
 *   · auto-scroll on new messages
 *   · optional TTS auto-read of the finished assistant reply
 *
 * This isolates all thread-state and streaming logic out of the
 * SanctaChatbot component so the parent can focus on layout and props.
 */
import { useEffect, useRef, useState } from "react";
import { api, getToken } from "@/lib/api";
import { SANCTA_LANGUAGES } from "@/hooks/useSanctaLocale";

const API = process.env.REACT_APP_BACKEND_URL as string;
const CONVO_STORAGE_KEY = "sb_sancta_convo_v1";

export interface SanctaThreadMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: number;
  detectedLang?: string;
}

export interface SanctaDetection {
  code: string;
  detected: string;
  confidence: number;
  source: string;
  conversation_id: string;
}

export interface UseSanctaThreadOptions {
  /** Auth user (or null for guests). Drives server-recall of latest thread. */
  user: any;
  /** Current page context sent with every message. */
  pageCtx: Record<string, any>;
  /** Whether the widget is open — gates history load + auto-scroll. */
  open: boolean;
  /** Language pick (widget UI language). */
  langCode: string;
  setLangCode: (code: string) => void;
  /** Auto-detect language on inbound text (Sancta will switch to it). */
  autoSwap: boolean;
  /** Web Speech Synthesis hook returned by useSpeechSynthesis(). */
  tts: {
    supported: boolean;
    speaking: boolean;
    speak: (id: string, text: string, lang: string) => void;
    stop: () => void;
  };
  /** Auto-read Sancta's replies aloud as soon as they finish streaming. */
  autoRead: boolean;
}

export interface UseSanctaThreadResult {
  messages: SanctaThreadMessage[];
  input: string;
  setInput: (v: string) => void;
  streaming: boolean;
  hydratingHistory: boolean;
  conversationId: string | null;
  detection: SanctaDetection | null;
  listRef: React.MutableRefObject<HTMLDivElement | null>;
  sendMessage: (text: string) => Promise<void>;
  handleSubmit: (e: React.FormEvent) => void;
  handleStarter: (text: string) => void;
}

export function useSanctaThread({
  user, pageCtx, open, langCode, setLangCode, autoSwap, tts, autoRead,
}: UseSanctaThreadOptions): UseSanctaThreadResult {
  const [messages, setMessages] = useState<SanctaThreadMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [detection, setDetection] = useState<SanctaDetection | null>(null);
  const [hydratingHistory, setHydratingHistory] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const historyLoadedRef = useRef<string | null>(null);
  const wasStreamingRef = useRef(false);

  // Restore prior conversation ID: prefer the server's "my latest" for
  // logged-in users (survives localStorage wipes + cross-device), fall
  // back to the tab's localStorage for guests / brand-new sessions.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (user) {
        try {
          const { data } = await api.get("/sancta/conversations/mine/latest");
          if (cancelled) return;
          if (data?.conversation_id) {
            setConversationId(data.conversation_id);
            try { localStorage.setItem(CONVO_STORAGE_KEY, data.conversation_id); } catch {}
            return;
          }
        } catch { /* fall through to localStorage */ }
      }
      try {
        const saved = localStorage.getItem(CONVO_STORAGE_KEY);
        if (!cancelled && saved) setConversationId(saved);
      } catch { /* SSR */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // On open, fetch prior thread if we have a conversation id. Only fires
  // once per widget-open per conversation, never during a live stream —
  // otherwise the SSE `event: lang` frame's setConversationId would
  // trigger a history refetch that overwrites the streaming reply.
  useEffect(() => {
    if (!open || !conversationId) return;
    if (streaming) return;
    if (historyLoadedRef.current === conversationId) return;
    historyLoadedRef.current = conversationId;
    // Show a skeleton while the /conversations/{id} round-trip is in
    // flight so the widget doesn't flash empty on reopen with long
    // history.
    setHydratingHistory(true);
    fetch(`${API}/api/sancta/conversations/${conversationId}`)
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d.messages)) {
          setMessages(d.messages.map((m: any, idx: number) => ({
            id: `h_${idx}_${m.created_at || idx}`,
            role: m.role,
            content: m.content,
            createdAt: Date.parse(m.created_at) || Date.now(),
            detectedLang: m.effective_language || m.detected_language,
          })));
        }
      })
      .catch(() => {})
      .finally(() => setHydratingHistory(false));
  }, [open, conversationId, streaming]);

  // Reset the history-loaded guard when the widget closes so re-opening
  // fetches the latest thread again.
  useEffect(() => { if (!open) historyLoadedRef.current = null; }, [open]);

  // Auto-scroll to the bottom on every new message.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // When streaming finishes, if auto-read is on, read the last assistant
  // reply aloud in whichever language we settled on for that turn.
  useEffect(() => {
    const justFinished = wasStreamingRef.current && !streaming;
    wasStreamingRef.current = streaming;
    if (!justFinished) return;
    if (!autoRead || !tts.supported) return;
    if (!open) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    const body = (last.content || "").trim();
    if (!body) return;
    const readLang = last.detectedLang || detection?.code || langCode;
    const t = setTimeout(() => tts.speak(last.id, body, readLang), 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming, autoRead, open]);

  // Stop any active read-aloud when the widget is closed.
  useEffect(() => {
    if (!open && tts.speaking) tts.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sendMessage = async (text: string): Promise<void> => {
    if (!text.trim() || streaming) return;
    if (tts.speaking) tts.stop();
    const now = Date.now();
    const userMsg: SanctaThreadMessage = { id: `u_${now}`, role: "user", content: text.trim(), createdAt: now };
    const asstMsg: SanctaThreadMessage = { id: `a_${now}`, role: "assistant", content: "", createdAt: now };
    setMessages(m => [...m, userMsg, asstMsg]);
    setInput("");
    setStreaming(true);

    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      const token = getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API}/api/sancta/message`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          conversation_id: conversationId,
          message: text.trim(),
          language_hint: langCode,
          page_context: pageCtx,
        }),
        signal: abortController.signal,
      });
      if (!res.ok || !res.body) throw new Error(`Sancta HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      let done = false;
      let currentEvent: string | null = null;
      let turnDetection: SanctaDetection | null = null;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (!value) continue;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE frames — split on double newline
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          const lines = frame.split("\n");
          let eventName: string | null = null;
          const dataParts: string[] = [];
          for (const line of lines) {
            if (line.startsWith("event:")) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith("data:")) {
              dataParts.push(line.slice(5).trimStart());
            }
          }
          const payload = dataParts.join("\n");
          currentEvent = eventName || currentEvent;

          if (eventName === "lang") {
            try {
              const parsed = JSON.parse(payload) as SanctaDetection;
              turnDetection = parsed;
              setDetection(parsed);
              if (parsed.conversation_id) {
                setConversationId(parsed.conversation_id);
                try { localStorage.setItem(CONVO_STORAGE_KEY, parsed.conversation_id); } catch {}
              }
              // Auto-swap widget UI to detected language if the user
              // hasn't explicitly opted out AND detection is confident.
              if (autoSwap && parsed.confidence >= 0.7 && parsed.code !== langCode) {
                if (SANCTA_LANGUAGES.some(l => l.code === parsed.code)) {
                  setLangCode(parsed.code);
                }
              }
            } catch { /* malformed frame — skip */ }
            continue;
          }

          if (!payload) continue;
          if (payload === "[[END]]") {
            done = true;
            break;
          }
          // Un-escape our simple \n escape
          const chunk = payload.replace(/\\n/g, "\n");
          acc += chunk;
          setMessages(m => {
            const last = m[m.length - 1];
            if (last?.role !== "assistant") return m;
            return [
              ...m.slice(0, -1),
              { ...last, content: acc, detectedLang: turnDetection?.code || last.detectedLang },
            ];
          });
        }
      }
    } catch (e: any) {
      if (e.name !== "AbortError") {
        setMessages(m => {
          const last = m[m.length - 1];
          if (last?.role !== "assistant") return m;
          return [...m.slice(0, -1), { ...last, content: "Sancta hit a snag. Please try again." }];
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) sendMessage(input);
  };

  const handleStarter = (text: string) => { sendMessage(text); };

  return {
    messages,
    input,
    setInput,
    streaming,
    hydratingHistory,
    conversationId,
    detection,
    listRef,
    sendMessage,
    handleSubmit,
    handleStarter,
  };
}

export default useSanctaThread;
